import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared/enum";

import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import EntityTypesSupportedInFrequencyDuringActivity from "@app/types/dashboard/cards/frequencyDuringActivity/EntityTypesSupportedInFrequencyDuringActivity";
import FrequencyDuringActivityCardConfiguration from "@app/types/dashboard/cards/frequencyDuringActivity/FrequencyDuringActivityCardConfiguration";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import { createEmptyFilterModel } from "@app/util/filters/utils";

import {
  applyFrequencyDuringActivityDrillDownListViewSettings,
  fetchFrequencyDuringActivityCardDrillDownData,
  FrequencyDuringActivityCardActions,
  hideFrequencyDuringActivityDrillDown,
  showFrequencyDuringActivityDrillDown,
} from "./actions";

export interface FrequencyDuringActivityCardState {
  configuration?: FrequencyDuringActivityCardConfiguration; // also works as a "visible" trigger, visible if set
  loading?: boolean;
  totalFilteredRecords: Record<EntityTypesSupportedInFrequencyDuringActivity, number>;
  totalRecords: Record<EntityTypesSupportedInFrequencyDuringActivity, number>;
  viewStates: Record<EntityTypesSupportedInFrequencyDuringActivity, DrillDownViewState>;
}

export const frequencyDuringActivityInitialState: FrequencyDuringActivityCardState = {
  totalFilteredRecords: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInFrequencyDuringActivity, number>
  ),
  totalRecords: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInFrequencyDuringActivity, number>
  ),
  viewStates: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => {
      result[entityType] = {
        columns: [],
        filter: createEmptyFilterModel(),
        range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
        sort: [],
      };
      return result;
    },
    {} as Record<EntityTypesSupportedInFrequencyDuringActivity, DrillDownViewState>
  ),
};

const frequencyDuringActivityState = createReducer<
  FrequencyDuringActivityCardState,
  FrequencyDuringActivityCardActions
>(frequencyDuringActivityInitialState)
  .handleAction(showFrequencyDuringActivityDrillDown.request, (state, { payload }) => ({
    ...state,
    configuration: payload.configuration,
  }))
  .handleAction(showFrequencyDuringActivityDrillDown.success, (state, { payload }) => ({
    ...state,
    viewStates: payload.viewStates,
  }))
  .handleAction(hideFrequencyDuringActivityDrillDown, (state) => ({
    ...state,
    configuration: undefined,
  }))
  .handleAction(fetchFrequencyDuringActivityCardDrillDownData.request, (state) => ({
    ...state,
    loading: true,
  }))
  .handleAction(fetchFrequencyDuringActivityCardDrillDownData.success, (state, { payload }) => ({
    ...state,
    loading: false,
    totalFilteredRecords: {
      ...state.totalFilteredRecords,
      [payload.entityType]: payload.totalFilteredRecords,
    },
    totalRecords: {
      ...state.totalRecords,
      [payload.entityType]: payload.totalRecords,
    },
  }))
  .handleAction(fetchFrequencyDuringActivityCardDrillDownData.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(applyFrequencyDuringActivityDrillDownListViewSettings, (state, { payload }) => {
    const viewState = state.viewStates[payload.entityType];
    return {
      ...state,
      viewStates: {
        ...state.viewStates,
        [payload.entityType]: {
          ...viewState,
          columns: payload.columns ?? viewState.columns,
          filter: payload.filter ?? viewState.filter,
          range: payload.range ?? viewState.range,
          sort: payload.sort ?? viewState.sort,
        },
      },
    };
  });

export default frequencyDuringActivityState;
